import React, { useCallback, useEffect, useState } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    IconButton,
    Chip,
    InputAdornment,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    CircularProgress,
    Stack,
    Card,
    CardContent
} from '@mui/material';
import {
    Add as IconAdd,
    Edit as IconEdit,
    Search as IconSearch,
    QrCode as IconBarcode,
    Print as IconPrint,
    CheckCircle as IconCheck,
    Cancel as IconCancel,
    Inventory2 as IconInventory,
    WarningAmber as IconWarning
} from '@mui/icons-material';

import { getsArticulo, postArticulo, cambiarEstadoArticulo, getsCategoriasArticulo } from '../../services/inventarioService';
import FiltroFilial from '../../components/common/FiltroFilial';
import { useFiltroFilial } from '../../hooks/useFiltroFilial';
import ArticuloDialog from '../../components/Inventario/ArticuloDialog';
import CodigoBarras from '../../components/common/CodigoBarras';

const ArticuloIndex = () => {
    const { filiales, idFilial, setIdFilial, bloqueado } = useFiltroFilial();

    const [articulos, setArticulos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [cargando, setCargando] = useState(false);

    // Filtros
    const [busqueda, setBusqueda] = useState('');
    const [idCategoria, setIdCategoria] = useState(0);
    const [soloActivos, setSoloActivos] = useState(true);

    // Dialogo crear/editar
    const [dialogOpen, setDialogOpen] = useState(false);
    const [articuloSeleccionado, setArticuloSeleccionado] = useState(null);

    // Dialogo imprimir codigo de barras
    const [barcodeModalOpen, setBarcodeModalOpen] = useState(false);
    const [articuloBarcode, setArticuloBarcode] = useState(null);

    const cargarCategorias = useCallback(async () => {
        try {
            const data = await getsCategoriasArticulo();
            setCategorias(data || []);
        } catch (err) {
            console.error('Error cargando categorias:', err);
        }
    }, []);

    const cargarArticulos = useCallback(async () => {
        setCargando(true);
        try {
            const data = await getsArticulo({
                query: busqueda,
                idCategoria: idCategoria || 0,
                idFilial: idFilial || 0,
                soloActivos
            });
            setArticulos(data || []);
        } catch (err) {
            console.error('Error cargando articulos:', err);
        } finally {
            setCargando(false);
        }
    }, [busqueda, idCategoria, idFilial, soloActivos]);

    useEffect(() => {
        cargarCategorias();
    }, [cargarCategorias]);

    useEffect(() => {
        cargarArticulos();
    }, [cargarArticulos]);

    const handleNuevo = () => {
        setArticuloSeleccionado(null);
        setDialogOpen(true);
    };

    const handleEditar = (item) => {
        setArticuloSeleccionado(item);
        setDialogOpen(true);
    };

    const handleGuardar = async (formData) => {
        try {
            await postArticulo(formData);
            setDialogOpen(false);
            cargarArticulos();
        } catch (err) {
            alert('Error al guardar el artículo: ' + (err.response?.data?.message || err.message));
        }
    };

    const handleToggleEstado = async (item) => {
        const nuevoEstado = !item.activo;
        if (window.confirm(`¿Desea ${nuevoEstado ? 'activar' : 'desactivar'} el artículo "${item.nombre}"?`)) {
            try {
                await cambiarEstadoArticulo(item.id, nuevoEstado);
                cargarArticulos();
            } catch (err) {
                alert('Error al cambiar estado.');
            }
        }
    };

    const handleAbrirBarcode = (item) => {
        setArticuloBarcode(item);
        setBarcodeModalOpen(true);
    };

    const handleImprimirBarcode = () => {
        window.print();
    };

    return (
        <Box sx={{ p: 3 }}>
            {/* Cabecera */}
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#1f2937', display: 'flex', alignItems: 'center', gap: 1 }}>
                        <IconInventory color="primary" /> Catálogo de Artículos y Materiales
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Gestión de insumos clínicos, materiales terapéuticos, existencias por filial y códigos de barras.
                    </Typography>
                </Box>
                <Button
                    variant="contained"
                    color="primary"
                    startIcon={<IconAdd />}
                    onClick={handleNuevo}
                    sx={{ textTransform: 'none', fontWeight: 'bold' }}
                >
                    Nuevo Artículo
                </Button>
            </Stack>

            {/* Filtros */}
            <Paper variant="outlined" sx={{ p: 2, mb: 3, bgcolor: '#ffffff' }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                        <FiltroFilial
                            filiales={filiales}
                            idFilial={idFilial}
                            setIdFilial={setIdFilial}
                            bloqueado={bloqueado}
                        />
                    </Grid>
                    <Grid item xs={12} sm={3}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Categoría / Grupo</InputLabel>
                            <Select
                                value={idCategoria}
                                onChange={(e) => setIdCategoria(e.target.value)}
                                label="Categoría / Grupo"
                            >
                                <MenuItem value={0}>-- Todas las Categorías --</MenuItem>
                                {categorias.map((c) => (
                                    <MenuItem key={c.id} value={c.id}>
                                        {c.descripcion}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <TextField
                            size="small"
                            fullWidth
                            placeholder="Buscar por código, nombre o código de barras..."
                            value={busqueda}
                            onChange={(e) => setBusqueda(e.target.value)}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <IconSearch fontSize="small" />
                                    </InputAdornment>
                                )
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} sm={2}>
                        <FormControl fullWidth size="small">
                            <InputLabel>Estado</InputLabel>
                            <Select
                                value={soloActivos ? 1 : 0}
                                onChange={(e) => setSoloActivos(e.target.value === 1)}
                                label="Estado"
                            >
                                <MenuItem value={1}>Solo Activos</MenuItem>
                                <MenuItem value={0}>Todos (Inc. Inactivos)</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                </Grid>
            </Paper>

            {/* Tabla de Artículos */}
            <Paper variant="outlined" sx={{ overflow: 'hidden' }}>
                <TableContainer sx={{ maxHeight: 650 }}>
                    <Table stickyHeader size="small">
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f3f4f6' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Código Barras</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Nombre del Artículo</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Categoría</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="center">U.M.</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Stock Filial</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Stock Total</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="right">Costo Unit.</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="center">Estado</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }} align="center">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {cargando ? (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                                        <CircularProgress size={30} />
                                    </TableCell>
                                </TableRow>
                            ) : articulos.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                        No se encontraron artículos registrados.
                                    </TableCell>
                                </TableRow>
                            ) : (
                                articulos.map((art) => {
                                    const stockSede = art.stockFilial != null ? art.stockFilial : art.stockActual;
                                    const esBajoStock = stockSede <= art.stockMinimo;

                                    return (
                                        <TableRow key={art.id} hover>
                                            <TableCell sx={{ fontFamily: 'monospace', fontWeight: 'bold' }}>
                                                {art.codigo}
                                            </TableCell>
                                            <TableCell>
                                                {art.codigoBarras ? (
                                                    <Chip
                                                        icon={<IconBarcode />}
                                                        label={art.codigoBarras}
                                                        size="small"
                                                        variant="outlined"
                                                        onClick={() => handleAbrirBarcode(art)}
                                                        sx={{ cursor: 'pointer', fontFamily: 'monospace' }}
                                                    />
                                                ) : '-'}
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#111827' }}>
                                                    {art.nombre}
                                                </Typography>
                                                {art.descripcion && (
                                                    <Typography variant="caption" color="text.secondary">
                                                        {art.descripcion}
                                                    </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>{art.categoria}</TableCell>
                                            <TableCell align="center">
                                                <Chip label={art.unidadMedida} size="small" />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        fontWeight: 'bold',
                                                        color: esBajoStock ? '#dc2626' : '#15803d',
                                                        display: 'inline-flex',
                                                        alignItems: 'center',
                                                        gap: 0.5
                                                    }}
                                                >
                                                    {esBajoStock && <IconWarning fontSize="inherit" color="error" />}
                                                    {Number(stockSede).toFixed(2)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell align="right">
                                                {Number(art.stockActual).toFixed(2)}
                                            </TableCell>
                                            <TableCell align="right">
                                                S/ {Number(art.costoUnitarioPromedio).toFixed(2)}
                                            </TableCell>
                                            <TableCell align="center">
                                                <Chip
                                                    label={art.activo ? 'Activo' : 'Inactivo'}
                                                    color={art.activo ? 'success' : 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <Stack direction="row" spacing={0.5} justifyContent="center">
                                                    <IconButton
                                                        size="small"
                                                        color="primary"
                                                        onClick={() => handleEditar(art)}
                                                        title="Editar Artículo"
                                                    >
                                                        <IconEdit fontSize="small" />
                                                    </IconButton>
                                                    <IconButton
                                                        size="small"
                                                        color={art.activo ? 'error' : 'success'}
                                                        onClick={() => handleToggleEstado(art)}
                                                        title={art.activo ? 'Desactivar' : 'Activar'}
                                                    >
                                                        {art.activo ? <IconCancel fontSize="small" /> : <IconCheck fontSize="small" />}
                                                    </IconButton>
                                                </Stack>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Modal Crear / Editar Artículo */}
            <ArticuloDialog
                open={dialogOpen}
                onClose={() => setDialogOpen(false)}
                onSave={handleGuardar}
                articulo={articuloSeleccionado}
                categorias={categorias}
            />

            {/* Modal Impresión Código de Barras */}
            <Dialog open={barcodeModalOpen} onClose={() => setBarcodeModalOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                    Etiqueta de Código de Barras
                </DialogTitle>
                <DialogContent dividers sx={{ textAlign: 'center', py: 3 }}>
                    {articuloBarcode && (
                        <Box sx={{ p: 2, border: '1px dashed #cbd5e1', borderRadius: 2, display: 'inline-block' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                                {articuloBarcode.nombre}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
                                Código: {articuloBarcode.codigo} | Categoría: {articuloBarcode.categoria}
                            </Typography>
                            <CodigoBarras valor={articuloBarcode.codigoBarras} alto={60} ancho={2.5} />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
                    <Button onClick={() => setBarcodeModalOpen(false)} color="inherit">
                        Cerrar
                    </Button>
                    <Button variant="contained" startIcon={<IconPrint />} onClick={handleImprimirBarcode}>
                        Imprimir Etiqueta
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ArticuloIndex;
